import i18n from '@/utils/i18n';
import React from 'react';
import {
  AvailabilityMap,
  ItemListOption,
  ItemListOptionMetadata,
} from '@/contexts/item-context-types';
import { BORDER } from '@/styles';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { DefaultOrderableItemListItemActions } from './orderable-item-list-item-actions';
import {
  getVisibleQuantity,
  QuantityStateType,
} from '@/screens/new-order/hooks/use-quantity-reducer';
import { Note } from '@/components/note';
import { NoteType } from '@/enums';
import { OrderGuideInfo } from '@/components/orders/order-guide-info';
import { Platform, View } from 'react-native';
import { RenderActionsFn } from '@/components/orders/item-detail-actions';
import { UpdateQuantityFn } from '@/contexts/new-order-actions-context-types';
import { useGetOrderGuideItem } from '@/hooks/use-get-order-guide-item';
import { useSupplierDynamicConfigValue } from '@/hooks/use-dynamic-config-value';
import { VariantPackOrderInfo } from '@/components/orders/variant-pack-order-info';

export interface Props {
  availabilityMap?: AvailabilityMap;
  canDecrementToZero?: boolean;
  disabled?: boolean;
  disableTextInput?: boolean;
  inDraggableSheet?: boolean;
  isFocused: boolean;
  isInboundOrder?: boolean;
  onNotePress?: (metadata: ItemListOptionMetadata, type?: NoteType) => void;
  option: ItemListOption;
  quantityState?: QuantityStateType;
  renderActions?: RenderActionsFn;
  renderLastOrderedMessage: boolean;
  renderNote?: (skuMetadata: ItemListOptionMetadata) => React.ReactNode;
  renderOrderGuideButtonAction?: (variantPackUUID: string) => React.ReactNode;
  renderParLevels: boolean;
  renderSalesRepContent: (
    skuMetadata: ItemListOptionMetadata,
  ) => React.ReactNode;
  sampleRequestButton?: React.ReactNode;
  shouldHideSampleRequest: boolean;
  showTooltipOnMount?: boolean;
  showVariantPackOrderInfo?: boolean;
  skus: ItemListOptionMetadata[];
  updateQuantity?: UpdateQuantityFn;
}

export const MultiOptionOrderableItem: React.FC<Props> = ({
  availabilityMap,
  canDecrementToZero,
  disabled,
  disableTextInput,
  inDraggableSheet,
  isFocused,
  isInboundOrder,
  onNotePress,
  quantityState,
  renderActions,
  renderLastOrderedMessage,
  renderNote,
  renderOrderGuideButtonAction,
  renderParLevels,
  renderSalesRepContent,
  sampleRequestButton,
  shouldHideSampleRequest,
  showTooltipOnMount,
  showVariantPackOrderInfo,
  updateQuantity,
  skus,
}) => {
  const getOrderGuideItem = useGetOrderGuideItem();
  const isOrderNoteEnabled = useSupplierDynamicConfigValue(
    'line_item_notes_config',
  )?.enabled;
  const [styles] = useStyles();

  return (
    <View collapsable={false}>
      {skus.map((skuMetadata, index) => {
        const orderGuideItem = getOrderGuideItem(skuMetadata);
        const nonMinimalSkuHeader = showVariantPackOrderInfo ? (
          <VariantPackOrderInfo variantPackMetadata={skuMetadata} />
        ) : (
          <OrderGuideInfo
            isLastOrderedMessageVisible={renderLastOrderedMessage}
            isParLevelsVisible={renderParLevels}
            orderGuidePartitionUUID={skuMetadata.orderGuidePartitionUUID}
            variantPackUUID={skuMetadata.variantPackUUID}
          />
        );

        const { memo, notes, request } =
          orderGuideItem?.order_guide_restaurant || {};
        const hasNotes = Boolean(isOrderNoteEnabled ? memo || request : notes);

        return (
          <View
            key={`orderable-item-list-item-${
              skuMetadata.variantPackUUID
            }-${skuMetadata.paidSearch ? 'paid-search' : 'base'}`}
            style={[
              index === 0 ? null : styles.multiSkuWrapper,
              isInboundOrder ? styles.minimalSkuWrapper : null,
            ]}
          >
            {isInboundOrder ? null : nonMinimalSkuHeader}
            <View style={BORDER.primary}>
              <DefaultOrderableItemListItemActions
                canDecrementToZero={canDecrementToZero}
                disabled={disabled}
                disableTextInput={disableTextInput}
                inDraggableSheet={inDraggableSheet}
                isFocused={index === 0 && isFocused}
                metadata={skuMetadata}
                quantity={
                  quantityState &&
                  getVisibleQuantity(quantityState, skuMetadata.variantPackUUID)
                }
                quantityInStock={availabilityMap?.[skuMetadata.variantPackUUID]}
                renderActions={renderActions}
                renderOrderGuideButton={renderOrderGuideButtonAction}
                showTooltipOnMount={showTooltipOnMount}
                updateQuantity={updateQuantity}
              />
              {renderSalesRepContent(skuMetadata)}
            </View>
            {isInboundOrder && showVariantPackOrderInfo ? (
              <VariantPackOrderInfo variantPackMetadata={skuMetadata} />
            ) : null}

            {/* intentionally not checking for orderGuideItem before renderNote() 
            because getOrderGuideItem returns null for search results (orderGuidePartitionUUID is null) */}
            {renderNote ? (
              renderNote(skuMetadata)
            ) : onNotePress ? (
              hasNotes && orderGuideItem ? (
                <View style={styles.noteContainer}>
                  <Note
                    onPress={(type) => onNotePress(skuMetadata, type)}
                    orderGuideItem={orderGuideItem}
                  />
                </View>
              ) : Platform.OS === 'web' ? (
                <View style={styles.noteButtonContainer}>
                  <Button
                    focusable={false}
                    onPress={() => onNotePress(skuMetadata)}
                    size="xsmall"
                    style={styles.addNoteButtonText}
                    trackingData={{
                      name: 'orderable_item_list_item.add_note_button',
                      properties: {
                        variantPackUUID: skuMetadata.variantPackUUID,
                      },
                    }}
                    type="tertiary"
                  >
                    {i18n.t('add_note')}
                  </Button>
                </View>
              ) : null
            ) : null}
          </View>
        );
      })}
      {shouldHideSampleRequest || !sampleRequestButton
        ? null
        : sampleRequestButton}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  addNoteButtonText: {
    borderWidth: 0,
    height: 16,
    marginTop: 4,
    paddingHorizontal: 0,
  },
  minimalSkuWrapper: {
    gap: 4,
  },
  multiSkuWrapper: {
    marginTop: 8,
  },
  noteButtonContainer: {
    paddingTop: 16,
  },
  noteContainer: {
    marginTop: theme.sk.spacing.spaceXsmall,
  },
}));
